/* Print-job rules, ported read-only from the StudioCore desktop Print Preview so the pack gets
 * exactly the payload it is tested with (m3-connect-and-print.md "Desktop rules"). */
import i18n from '../../i18n/index.js';

/** Quality numbers the pack understands (desktop default 4). Labels follow handoff screen 5. */
export const QUALITY = [
  { value: '2', label: 'print.qualityOpt.draft' },
  { value: '3', label: 'print.qualityOpt.fast' },
  { value: '4', label: 'print.qualityOpt.normal' },
  { value: '5', label: 'print.qualityOpt.best' },
];
export const DEFAULT_QUALITY = 4;
export const estimatorQuality = (q) => (q <= 3 ? 'fast' : q === 4 ? 'normal' : 'best');

/** Status text → tone, same regexes as the desktop paper picker. */
export function tone(status) {
  const s = String(status || '').toLowerCase();
  if (/not ready|missing|empty|out|error|unavailable|no media|no paper/.test(s)) return 'error';
  if (/low|warning/.test(s)) return 'warn';
  if (/ready|standby|active|ok|available/.test(s)) return 'ok';
  return 'idle';
}
const EMOJI = { error: '🔴', warn: '🟡', ok: '🟢', idle: '⚪' };

/** desktop derivePaperGeom(dev) */
export function paperGeom(dev) {
  if (!dev) return null;
  const widthIn = parseFloat(dev.widthIn);
  const label = `${EMOJI[tone(dev.status)]} ${dev.name || dev.type || i18n.t('bv:print.paper')}${Number.isFinite(widthIn) ? ` ${widthIn}"` : ''}`;
  return {
    label,
    widthIn: Number.isFinite(widthIn) ? widthIn : null,
    minMarginIn: parseFloat(dev.minMarginIn) || 0,
    mediaId: dev.mediaId != null ? String(dev.mediaId) : null,
    paper: dev.paper || null,
    isRoll: dev.isRoll === true,
    rollIndex: /roll\s*2/i.test(label) ? 2 : /roll\s*1/i.test(label) ? 1 : undefined,
  };
}
export const sourceValue = (dev) => dev.name || dev.type || 'unknown';
/** Some firmware (T650: LEDM media XML) lists a placeholder per bin type — "Roll" / "Single Sheet", Not Ready, width 0 —
 * next to the loaded bins, so the picker showed two Rolls and two Single Sheets, and the saved selection ("Roll") hit the
 * empty placeholder first ("Roll has no paper" while 36" plain was loaded). Per name: keep the ready bins (one per
 * width + paper); with none ready, keep a single not-ready entry. Loaded bins come first, then the empty ones. */
export function dedupeSources(inputs) {
  const out = []; const seen = new Map(); // name → { ready: Set(width|paper), idle: index }
  for (const d of inputs || []) {
    const name = sourceValue(d); const ready = tone(d.status) !== 'error';
    let g = seen.get(name); if (!g) { g = { ready: new Set(), idle: -1 }; seen.set(name, g); }
    if (ready) {
      const key = `${parseFloat(d.widthIn) || 0}|${d.paper || ''}`;
      if (g.ready.has(key)) continue;
      g.ready.add(key);
      if (g.idle >= 0) { out.splice(g.idle, 1, d); g.idle = -1; continue; } // the loaded bin replaces its placeholder in place
      out.push(d);
    } else if (g.ready.size === 0 && g.idle < 0) { g.idle = out.length; out.push(d); }
  }
  return [...out.filter((d) => tone(d.status) !== 'error'), ...out.filter((d) => tone(d.status) === 'error')];
}
export function sourceLabel(dev) {
  const w = parseFloat(dev.widthIn);
  return [dev.name || dev.type || i18n.t('bv:print.paper'), Number.isFinite(w) && w > 0 ? `${w} in` : null, dev.paper || null, tone(dev.status) === 'error' ? i18n.t('bv:print.noPaper') : null].filter(Boolean).join(' · ');
}

/** Pack family from the model name (pack/registry.py FAMILY_ORDER: specific tokens first). */
const FAMILIES = [['Z6Z9', ['z6', 'z9']], ['XT870', ['xt870']], ['T600', ['t630', 't650']], ['T500', ['t520', 't530']],
  ['T200', ['t210', 't230', 't250']], ['T850', ['t850', 't870', 't950']], ['CANON_TM', ['tm-205']]];
export function family(model) {
  const m = String(model || '').toLowerCase();
  const hit = FAMILIES.find(([, toks]) => toks.some((t) => m.includes(t)));
  return hit ? hit[0] : null;
}
/** Z6/Z9 and XT870 size the ORIGINAL image themselves (customOriginalPath); the rest print a padded canvas. */
export const sizesOriginal = (model) => ['Z6Z9', 'XT870'].includes(family(model));

/** Desktop custom-size limits (PrintItemCard): width 8–60 and ≤ the printable width; height ≥ 8 (15 on T200) and ≤ 60. */
export function customLimits(model, geom) {
  const printable = geom && geom.widthIn ? geom.widthIn - 2 * (geom.minMarginIn || 0) : null;
  return { minW: 8, maxW: printable ? Math.min(60, printable) : null, minH: family(model) === 'T200' ? 15 : 8, maxH: 60 };
}
/** Desktop entering Custom (PP:2155): W = the printable width, H = W × the image's aspect (T200: at least 15); 24 × 36 with no paper. */
export function defaultCustom(file, model, geom) {
  const printable = geom && geom.widthIn ? geom.widthIn - 2 * (geom.minMarginIn || 0) : null;
  if (!printable) return { w: 24, h: 36 };
  const w = round2(printable);
  let h = file && file.w && file.h ? round2(w * (file.h / file.w)) : w;
  if (family(model) === 'T200') h = Math.max(15, h);
  return { w, h };
}
const round2 = (n) => Math.round(n * 100) / 100;
export function customError(c, model, geom) {
  if (!c) return null;
  const lim = customLimits(model, geom);
  if (!lim.maxW) return i18n.t('bv:print.custom.pickRoll');
  if (!(c.w >= lim.minW)) return i18n.t('bv:print.custom.minW', { n: lim.minW });
  if (c.w > lim.maxW + 1e-6) return i18n.t('bv:print.custom.maxW', { n: round2(lim.maxW) });
  if (!(c.h >= lim.minH)) return i18n.t('bv:print.custom.minH', { n: lim.minH });
  if (c.h > lim.maxH) return i18n.t('bv:print.custom.maxH', { n: lim.maxH });
  // The padded canvas is made in the browser at 300 dpi; keep it inside what browsers can hold.
  if (!sizesOriginal(model) && geom.widthIn * 300 * c.h * 300 > 160e6) return i18n.t('bv:print.custom.tooTall', { n: Math.floor(160e6 / (geom.widthIn * 300 * 300)) });
  return null;
}

/**
 * The desktop's custom canvas (custom_canvas_sizer.py): white, paper width × poster height at 300 dpi,
 * the poster resized to W × H and placed at the top left; JPEG 95.
 */
export async function customCanvas(fileOrUrl, c, paperWidthIn) {
  const DPI = 300;
  const src = typeof fileOrUrl === 'string' ? await (await fetch(fileOrUrl)).blob() : fileOrUrl;
  const bmp = await createImageBitmap(src);
  const canvas = document.createElement('canvas');
  canvas.width = Math.floor(paperWidthIn * DPI);
  canvas.height = Math.floor(c.h * DPI);
  const g = canvas.getContext('2d');
  g.fillStyle = '#ffffff';
  g.fillRect(0, 0, canvas.width, canvas.height);
  g.imageSmoothingQuality = 'high';
  g.drawImage(bmp, 0, 0, Math.floor(c.w * DPI), Math.floor(c.h * DPI));
  const blob = await new Promise((res) => canvas.toBlob(res, 'image/jpeg', 0.95));
  canvas.width = 1; canvas.height = 1; // free the pixels now
  if (!blob) throw new Error(i18n.t('bv:print.custom.browserFailed'));
  return new File([blob], 'custom.jpg', { type: 'image/jpeg' });
}

/** Tray / sheet-feeder sheets, every family (T200, T600/T650, T850, XT870 — the Z6 is roll-only): the print is scaled
 * down to fit the sheet height. Letter, A4, Tabloid, A3, 13×19 (Super B / A3+), 17×22 (C) and A2; an unknown width gets
 * no cap (desktop parity: the printer reports the length once the sheet is loaded). */
const SHEET_HEIGHT = { 8.5: 11, 8.27: 11.69, 11: 17, 11.69: 16.54, 13: 19, 17: 22, 16.54: 23.39 };
const sheetHeight = (w) => {
  for (const [k, h] of Object.entries(SHEET_HEIGHT)) if (Math.abs(Number(k) - w) < 0.1) return h;
  return null;
};

/**
 * Print size in inches for a file on a paper source. Portrait fills the printable width;
 * landscape turns the image 90° then fills it; on a tray sheet it is scaled to fit the sheet.
 */
export function printSize(file, geom) {
  if (!file || !file.w || !file.h || !geom || !geom.widthIn) return null;
  if (file.custom && geom.isRoll) return { paperWidthIn: geom.widthIn, widthIn: file.custom.w, heightIn: file.custom.h, sheet: null, custom: true };
  const landscape = file.orientation === 'landscape';
  const aspect = landscape ? file.w / file.h : file.h / file.w; // height / width as printed
  const printable = Math.max(1, geom.widthIn - 2 * (geom.minMarginIn || 0));
  let width = printable;
  let height = printable * aspect;
  let sheet = null;
  if (!geom.isRoll) {
    sheet = { width: geom.widthIn, height: sheetHeight(geom.widthIn) };
    const maxH = sheet.height ? sheet.height - 2 * (geom.minMarginIn || 0) : null;
    if (maxH && height > maxH) { width = width * (maxH / height); height = maxH; }
  }
  return { paperWidthIn: geom.widthIn, widthIn: width, heightIn: height, sheet };
}

/** The `print` payload (desktop handlePrint args, minus the custom-size fields). */
export function printPayload({ printer, geom, file, quality, copies }) {
  const custom = !!(file.custom && geom.isRoll);
  return {
    ip: printer.ip,
    model: printer.model,
    mediaId: Number(geom.mediaId || 1100),
    widthIn: Number(geom.widthIn),
    minMarginIn: custom ? 0 : Number(geom.minMarginIn || 0),
    paperLabel: geom.label,
    paperName: geom.paper,
    mode: !custom && file.orientation === 'landscape' ? 'landscape' : 'portrait',
    rollIndex: geom.rollIndex,
    isRoll: geom.isRoll,
    isCustomSize: custom,
    quality: Number(quality),
    verticalTrimmer: true,
    copies: Math.max(1, Math.min(50, Number(copies) || 1)),
    customOriginalPath: custom && sizesOriginal(printer.model) ? '$image' : null,
    ...(custom ? { customWidthIn: Number(file.custom.w), customHeightIn: Number(file.custom.h) } : {}),
    imagePath: '$image',
    clientPlatform: 'web',
    // Not read by the printer pack; shows the file in Settings ▸ Print history (Bridge print_job_logs.request).
    fileName: file.name || null,
  };
}

/* ---------------------------------------------------------------- ink */
const INK_COLORS = {
  // desktop getInkInfo abbreviations; colours in the Brightivity palette
  cyan: ['C', '#1AB0F2'], magenta: ['M', '#E5179B'], yellow: ['Y', '#FFD600'], black: ['B', '#1B2550'],
  'photo black': ['PB', '#1a1a1a'], 'matte black': ['MB', '#3f3f46'], 'chromatic red': ['CR', '#E8402A'], red: ['R', '#E8402A'],
  gray: ['G', '#8A8FA8'], grey: ['G', '#8A8FA8'], 'light cyan': ['LC', '#7FD6F7'], 'light magenta': ['LM', '#F48FCB'],
  'light gray': ['LG', '#B8BCCB'], 'light grey': ['LG', '#B8BCCB'], 'chromatic green': ['CG', '#26C774'], green: ['Gn', '#26C774'],
  'chromatic blue': ['CB', '#3B6CF6'], blue: ['Bl', '#3B6CF6'], 'chromatic yellow': ['CY', '#FFD600'], 'gloss enhancer': ['GE', '#D8DCE8'],
};
/** desktop getInkInfo: normalised name → short code + colour. */
export function inkInfo(ink) {
  const name = String(ink.color || '').toLowerCase().replace(/[-_]/g, ' ').replace(/\s+/g, ' ').trim();
  const hit = INK_COLORS[name] || (/(^|\s)black$/.test(name) ? ['B', '#1B2550'] : null);
  const code = hit ? hit[0] : name.split(' ').map((w) => w[0] || '').join('').slice(0, 3).toUpperCase() || '?';
  const percent = Math.max(0, Math.min(100, Math.round(Number(ink.percent) || 0)));
  return { code, color: hit ? hit[1] : '#94a3b8', name: ink.color || code, percent };
}
export const LOW_INK = 15; // desktop: warning under 15 %, critical under 5 %
export const CRITICAL_INK = 5;

const CHANNEL_DOT = { C: '#00BFFF', M: '#FF00FF', Y: '#FFD700', K: '#333', cR: '#FF4444', CR: '#FF4444', PK: '#888', MK: '#aaa' }; // desktop ink pill
export const channelDot = (k) => CHANNEL_DOT[k] || '#94a3b8';

/* ---------------------------------------------------------------- job progress (desktop printStatusModel) */
/** desktop describePrinterAlert: alert name → touchscreen wording. */
export function describePrinterAlert(name) {
  const n = String(name || '');
  let m;
  if ((m = n.match(/\/COVERS\/(\w+)\/OPEN/))) return i18n.t('bv:print.alert.closeCoverNamed', { which: m[1].replace('_', ' ').toLowerCase() });
  if ((m = n.match(/CARTRIDGE_([a-z-]+)_\d+\/RESEAT/i))) return i18n.t('bv:print.alert.reseat', { which: m[1].replace('-', ' ') });
  if ((m = n.match(/CARTRIDGE_([a-z-]+)_\d+\/(EMPTY|OUT|MISSING)/i))) return i18n.t('bv:print.alert.replace', { which: m[1].replace('-', ' ') });
  if ((m = n.match(/INTRAY_\d+_Roll(\d+)\/OUT_OF_MEDIA/))) return i18n.t('bv:print.alert.rollOut', { n: m[1] });
  if (/\/MEDIA\/INPUT\/OUT_OF_MEDIA/.test(n)) return i18n.t('bv:print.alert.outOfPaper');
  if (/PRINTHEAD/.test(n)) return i18n.t('bv:print.alert.printhead');
  if (/CABLE_DISCONNECTED/.test(n)) return i18n.t('bv:print.alert.cable');
  if (/JOBS_ON_HOLD_FOR_CONFLICT/.test(n)) return i18n.t('bv:print.alert.holdConflict');
  const cdm = {
    mediaJam: 'jam', jam: 'jam',
    coverOpen: 'closeCover', doorOpen: 'closeDoor', 'cover-open': 'closeCover', 'door-open': 'closeDoor',
    mediaEmpty: 'outOfPaper', 'media-empty': 'outOfPaper', 'media-needed': 'loadPaper',
    mediaMismatch: 'mismatch', cartridgeMissing: 'cartridgeMissing', cartridgeEmpty: 'cartridgeEmpty',
    'marker-supply-empty': 'cartridgeEmpty', 'marker-supply-low': 'inkLow', printheadMissing: 'printheadMissing', printheadFailure: 'printheadFailure',
  };
  if (cdm[n]) return i18n.t(`bv:print.alert.${cdm[n]}`);
  if (!n.includes('/')) return n.replace(/[-_]/g, ' ').replace(/([a-z])([A-Z])/g, '$1 $2').toLowerCase().replace(/^\w/, (c) => c.toUpperCase());
  return n.split('/').filter(Boolean).slice(-2).join(' ').replace(/_/g, ' ').toLowerCase().replace(/^\w/, (c) => c.toUpperCase());
}

const LFP_PREP = /^(Processing|WaitingToProcess|WaitingToPrint|QueuedInEngine|PreparingToPrint|CheckingDestination|WaitingForNest)$/;
const LFP_PRINT = /^(Printing|PrintingInANest)$/;
const LFP_FINISH = /^(Drying|Ejecting|Finishing|Folding|Cancelling|Servicing|WaitingForRetrieval|Completed)$/;
const LFP_HOLD = /^(OnHold|Paused|ConflictBlocked)$/;

/**
 * Events from the Bridge command record → toast view (desktop derivePrintView semantics).
 * {title, subtitle, pct|null, tone:'blue'|'green'|'coral'|'yellow', terminal, canCancel, ack}
 */
export function jobView({ events = [], status, deviceOnline, deviceName, cancelling }) {
  const end = [...events].reverse().find((e) => e.ev === 'result' || e.ev === 'error');
  if (end && end.ev === 'error') {
    if (end.code === 'CANCELLED') return { title: i18n.t('bv:print.job.cancelled'), subtitle: i18n.t('bv:print.job.cancelledSub'), pct: null, tone: 'yellow', terminal: true };
    return { title: i18n.t('bv:print.job.failed'), subtitle: friendlyError(end.msg || end.error, end.code), pct: null, tone: 'coral', terminal: true, error: true };
  }
  if (end) {
    const live = events.some((e) => e.ev === 'progress' && e.stage === 'printing');
    return live
      ? { title: i18n.t('bv:print.job.finished'), subtitle: i18n.t('bv:print.job.finishedSub'), pct: 100, tone: 'green', terminal: true }
      : { title: i18n.t('bv:print.job.sent'), subtitle: i18n.t('bv:print.job.sentSub'), pct: 100, tone: 'green', terminal: true };
  }
  if (cancelling) return { title: i18n.t('bv:print.job.cancelling'), subtitle: i18n.t('bv:common.pleaseWait'), pct: null, tone: 'yellow', canCancel: false };
  if (status === 'queued' && !deviceOnline) return { title: i18n.t('bv:print.job.waitingComputer'), subtitle: i18n.t('bv:print.job.waitingComputerSub', { name: deviceName || i18n.t('bv:print.job.yourComputer') }), pct: null, tone: 'yellow', canCancel: true };
  const p = [...events].reverse().find((e) => e.ev === 'progress');
  if (!p) return { title: i18n.t('bv:print.job.sendingComputer'), subtitle: i18n.t('bv:common.pleaseWait'), pct: null, tone: 'blue', canCancel: true };
  if (p.phase === 'canceling') return { title: i18n.t('bv:print.job.cancelling'), subtitle: i18n.t('bv:common.pleaseWait'), pct: null, tone: 'yellow', canCancel: false };
  // Helper 0.1.8+: queued behind another print to the same printer (the Helper runs one print per printer at a time)
  if (p.phase === 'printer_busy') return { title: i18n.t('bv:print.job.waitingToPrint'), subtitle: i18n.t('bv:print.job.printerBusySub'), pct: null, tone: 'yellow', canCancel: true };
  if (p.phase === 'lfp_cancel_unavailable') return { title: i18n.t('bv:print.job.printing'), subtitle: i18n.t('bv:print.job.noCancelHere'), pct: null, tone: 'yellow', canCancel: false };
  if (['start', 'load', 'raster', 'pwg_encode'].includes(p.phase) || (p.stage === 'preparing' && !String(p.phase || '').startsWith('lfp_'))) {
    return { title: i18n.t('bv:print.job.preparing'), subtitle: p.engine ? i18n.t('bv:print.job.gettingFile') : i18n.t('bv:common.pleaseWait'), pct: null, tone: 'blue', canCancel: true };
  }
  if (p.stage === 'sending' && Number(p.total) > 0 && p.phase !== 'sent') {
    const pct = Math.min(99, Math.round((100 * Number(p.bytes || 0)) / Number(p.total)));
    return { title: i18n.t('bv:print.job.printing'), subtitle: i18n.t('bv:print.job.pctSent', { pct }), pct, tone: 'blue', canCancel: true };
  }
  if (typeof p.phase === 'string' && p.phase.startsWith('lfp_')) {
    const st = String(p.status || '');
    const pctNum = Number(p.pct);
    const eta = Number(p.eta_s);
    const printing = LFP_PRINT.test(st);
    const finishing = LFP_FINISH.test(st);
    const held = LFP_HOLD.test(st);
    const act = String(p.activity || '');
    const errors = p.errors && p.errors !== '-' ? String(p.errors).split(',') : [];
    const waitingOnPanel = (printing || finishing) && (p.prompt === 'remove_print' || act === 'CheckingPrinter');
    if (p.phase === 'lfp_job_done') return { title: i18n.t('bv:print.job.printing'), subtitle: i18n.t('bv:print.job.finishing'), pct: 100, tone: 'blue', canCancel: true };
    if (held || errors.length) {
      const sub = errors.length ? describePrinterAlert(errors[0]) : p.hold && p.hold !== 'None' ? p.hold : (p.conflict && p.conflict !== '-' ? p.conflict : i18n.t('bv:print.job.checkScreen'));
      return { title: i18n.t('bv:print.job.attention'), subtitle: sub, pct: null, tone: 'coral', canCancel: true };
    }
    if (waitingOnPanel) return { title: i18n.t('bv:print.job.finished'), subtitle: i18n.t('bv:print.job.removePrint'), pct: 100, tone: 'green', ack: true };
    if (printing) {
      const pct = Number.isFinite(pctNum) ? Math.min(99, Math.max(0, Math.round(pctNum))) : null;
      const sub = pct == null ? i18n.t('bv:print.job.printingDots') : !(eta > 0) ? i18n.t('bv:print.job.pctComplete', { pct }) : eta < 60 ? i18n.t('bv:print.job.pctAlmost', { pct }) : i18n.t('bv:print.job.pctEta', { pct, min: Math.round(eta / 60) });
      return { title: i18n.t('bv:print.job.printing'), subtitle: sub, pct, tone: 'blue', canCancel: true };
    }
    if (finishing || act === 'Drying' || act === 'TerminatingPrint') return { title: i18n.t('bv:print.job.printing'), subtitle: i18n.t('bv:print.job.finishing'), pct: 100, tone: 'blue', canCancel: true };
    if (LFP_PREP.test(st)) return { title: i18n.t('bv:print.job.preparing'), subtitle: i18n.t('bv:print.job.printerPreparing'), pct: null, tone: 'blue', canCancel: true };
    return { title: i18n.t('bv:print.job.sendingJob'), subtitle: i18n.t('bv:print.job.received'), pct: null, tone: 'blue', canCancel: true };
  }
  if (p.stage === 'done' || ['sent', 'send_done', 'job_state'].includes(p.phase)) {
    return { title: i18n.t('bv:print.job.printing'), subtitle: i18n.t('bv:print.job.finishingConfirm'), pct: 100, tone: 'blue', canCancel: true };
  }
  return { title: i18n.t('bv:print.job.sendingJob'), subtitle: i18n.t('bv:print.job.received'), pct: null, tone: 'blue', canCancel: true };
}

/** IPP status codes the printer answers with → words a teacher can act on (raw text kept after a dash). */
const IPP_WORDS = { '0x0500': 'internal', '0x0503': 'fileType', '0x0506': 'notTaking', '0x0507': 'busyStopped', '0x0508': 'cancelledByPrinter', '0x040a': 'paperSize', '0x040b': 'paperSize' };
export function friendlyError(msg, code) {
  const m = String(msg || '');
  const hit = m.match(/0x0[45]0[0-9a-f]/i);
  if (hit && IPP_WORDS[hit[0].toLowerCase()]) return i18n.t(`bv:print.ipp.${IPP_WORDS[hit[0].toLowerCase()]}`);
  if (code === 'PRINTER_BUSY') return i18n.t('bv:print.err.busy');
  if (code === 'PRINT_STALLED') return i18n.t('bv:print.err.stalled');
  if (code === 'UNREACHABLE' || /timed out|unreachable|refused/i.test(m)) return i18n.t('bv:print.err.unreachable');
  return m.replace(/^\[ERROR\]\s*/, '') || i18n.t('bv:print.err.notTaken');
}

/** printer-live-status → the status pill. */
/** The printer's "out of paper" is one flag for the whole machine (IPP media-empty / media-needed): on an XT870 / T850 with
 * an empty roll and a loaded sheet feeder it still says out of paper. When the chosen source is a loaded non-roll bin, say so
 * instead of blocking the eye with a red "out of paper" (the print itself was never blocked; 2026-10-19). */
const MEDIA_ONLY = (live) => {
  const al = live.alerts || [];
  const media = (x) => /media-(empty|needed)|OUT_OF_MEDIA|load paper|out of paper/i.test(`${x.id || ''} ${x.category || ''} ${x.text || ''}`);
  return al.length ? al.every(media) : !!(live.primary && media(live.primary));
};
export function statusPill(live, info, dev) {
  if (live && live.ok && live.supported) {
    if (live.ipp && live.ipp.state === 4) return { tone: 'blue', text: `${i18n.t('bv:print.job.printing')}${live.ipp.queuedJobs > 1 ? ` · ${i18n.t('bv:print.inQueue', { count: live.ipp.queuedJobs })}` : ''}` };
    if (live.health === 'error' && dev && tone(dev.status) !== 'error' && parseFloat(dev.widthIn) > 0 && MEDIA_ONLY(live)) {
      const src = dev.name || dev.type || i18n.t('bv:print.paper');
      // a loaded sheet / tray source: the empty roll is not in the way
      if (!dev.isRoll) return { tone: 'yellow', text: i18n.t('bv:print.pill.rollEmptySourceOk', { source: src }) };
      // a loaded roll while the alerts name OTHER rolls only (Z6dr / dual-roll: "Roll 2 is out of paper" with Roll 1 chosen)
      const mine = String(src).match(/(\d+)/);
      const named = (live.alerts || []).map((x) => String(x.id || x.text || '').match(/Roll\s*(\d+)/i)).filter(Boolean).map((m) => m[1]);
      if (mine && named.length && !named.includes(mine[1])) return { tone: 'yellow', text: i18n.t('bv:print.pill.otherRollEmpty', { n: named[0], source: src }) };
    }
    if (live.health === 'error') return { tone: 'coral', text: (live.primary && live.primary.text) || i18n.t('bv:print.job.attention') };
    if (live.health === 'warning') return { tone: 'yellow', text: (live.primary && live.primary.text) || i18n.t('bv:print.pill.check') };
    // IPP printer-state 5 = stopped, or not accepting jobs: health can still say ok (seen on a T650, 2026-10-16).
    if (live.ipp && (live.ipp.state === 5 || live.ipp.acceptingJobs === false)) {
      return { tone: 'yellow', text: live.ipp.queuedJobs ? i18n.t('bv:print.pill.stoppedWaiting', { count: live.ipp.queuedJobs }) : i18n.t('bv:print.pill.stopped') };
    }
    return { tone: 'green', text: i18n.t('bv:print.pill.ready') };
  }
  if (live && live.ok === false) return { tone: 'coral', text: i18n.t('bv:print.pill.unreachable') };
  if (info && info.ok) return { tone: 'green', text: i18n.t('bv:print.pill.ready') };
  if (info && info.ok === false) return { tone: 'coral', text: i18n.t('bv:print.pill.unreachable') };
  return null;
}
